import Header from '@components/Shared/Layout/Header';
import SearchInput from '@components/Shared/SearchInput';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAddTeamMutation, useGetTeamsQuery } from '@store/api/teamEndpoints';
import { usePermission } from '@store/hooks';
import { useMemo, useState } from 'react';
import { AngleRightIcon, PlusIcon, TimesIcon, UserFriendsIcon } from 'react-line-awesome';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  buttonStyles,
  dialogCloseStyles,
  dialogFooterStyles,
  dialogHeaderStyles,
  dialogPaperStyles,
  teamIconStyles,
  teamRowStyles,
} from './Teams.styles';

const TeamsPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [newTeamOpen, setNewTeamOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data: teams = [], isLoading } = useGetTeamsQuery();
  const [addTeam, { isLoading: isCreating }] = useAddTeamMutation();

  const { can } = usePermission();
  const canCreateTeam = can('team:post');

  const filteredTeams = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return teams;
    return teams.filter((team) =>
      team.name.toLowerCase().includes(query) ||
      team.description?.toLowerCase().includes(query)
    );
  }, [teams, search]);

  const openTeam = (teamId: string) => navigate(generatePath(ROUTE_PATHS.TEAM_DETAIL, { teamId }));

  const closeNewTeam = () => {
    if (isCreating) return;
    setNewTeamOpen(false);
    setName('');
    setDescription('');
  };

  const canCreate = name.trim().length >= 2 && description.trim().length >= 2 && !isCreating;

  const handleCreateTeam = async () => {
    if (!canCreate) return;

    const result = await addTeam({ name: name.trim(), description: description.trim(), members: [] });

    if (!result.error) closeNewTeam();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        {/* Same toolbar as the folder and POI type lists: search on the left carrying the
            result count, actions on the right. No page title — the sidebar names it. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginBottom: '12px'
          }}
        >
          <SearchInput
            text={search}
            placeholder={t('Team_Search')}
            action={setSearch}
            count={filteredTeams.length}
          />
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            {canCreateTeam && (
              <Button onClick={() => setNewTeamOpen(true)} variant="contained" startIcon={<PlusIcon />}>
                {t('Team_New')}
              </Button>
            )}
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {isLoading && <LinearProgress />}

          {!isLoading && filteredTeams.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {t('Team_NotFound')}
            </Typography>
          )}

          {filteredTeams.map((team, index) => (
            <Box key={team._id}>
              {index > 0 && <Divider />}
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={teamRowStyles}
                onClick={() => openTeam(team._id)}
              >
                <Box sx={teamIconStyles}>
                  <UserFriendsIcon />
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }} noWrap>{team.name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {team.description}
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72, textAlign: 'right' }}>
                    {t('Team_Member_Count', { count: team.members.length })}
                  </Typography>
                  <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 18 }}>
                    <AngleRightIcon />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Paper>
      </Box>

      <Dialog
        open={canCreateTeam && newTeamOpen}
        onClose={closeNewTeam}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { ...dialogPaperStyles, maxWidth: 480 } } }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={dialogHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>{t('Team_Create_Title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('Team_Create_Subtitle')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeNewTeam} sx={dialogCloseStyles}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Stack spacing={2} sx={{ px: 2.5, py: 2.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('Team_Name')}
            placeholder={t('Team_Name_Placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isCreating}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label={t('Common_Description')}
            placeholder={t('Team_Description_Placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isCreating}
          />
        </Stack>

        <Divider />

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={dialogFooterStyles}>
          <Button color="inherit" size="small" onClick={closeNewTeam} sx={buttonStyles} disabled={isCreating}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={handleCreateTeam}
            sx={buttonStyles}
            disabled={!canCreate}
          >
            {t('Team_Create_Submit')}
          </Button>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default TeamsPage;
